import { callApi } from '@/utils/http-utils'

export interface SurveyQuestion {
  questionId: number
  clusterId: number
  questionText: string
}

export interface ResponseOption {
  responseOptionId: number
  responseOptionScore: number
  responseOptionText: string
}

export interface SurveyAttempt {
  surveyId: number
  studentId: string
  status: string
  createdDate: string
}

export interface SurveyResponse {
  questionId: number
  responseOptionId: number
}

export interface QuestionsResponse {
  questions: SurveyQuestion[]
  pagination: {
    currentPage: number
    pageSize: number
    totalQuestions: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export interface ResponseOptionsResponse {
  responseOptions: ResponseOption[]
}

export interface SurveyResults {
  attempt: SurveyAttempt
  clusterScores: Array<{
    clusterId: number
    totalScore: number
  }>
  responses: Array<{
    questionId: number
    questionText: string
    clusterId: number
    responseScore: number
    responseText: string
  }>
}

// Get paginated survey questions
export async function getSurveyQuestions(page = 1, pageSize = 5): Promise<QuestionsResponse> {
  const res = await callApi(`survey-api/questions?page=${page}&pageSize=${pageSize}`, { method: 'GET' })

  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const text = await res.text()
    throw new Error(`Expected JSON but got ${ct}. ${text.slice(0, 200)}`)
  }

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to fetch questions: ${errorText}`)
  }

  return res.json()
}

// Get response options (1-5 scale)
export async function getResponseOptions(): Promise<ResponseOptionsResponse> {
  const res = await callApi(`survey-api/response-options`, { method: 'GET' })

  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const text = await res.text()
    throw new Error(`Expected JSON but got ${ct}. ${text.slice(0, 200)}`)
  }

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to fetch response options: ${errorText}`)
  }

  return res.json()
}

// Create new survey attempt
export async function createSurveyAttempt(studentId: string): Promise<SurveyAttempt> {
  const res = await callApi(`survey-api/attempts`, {
    method: 'POST',
    body: { studentId },
    accept: 'application/json',
  })

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to create survey attempt: ${errorText}`)
  }

  return res.json()
}

// Save survey responses (batch)
export async function saveSurveyResponses(surveyId: number, responses: SurveyResponse[]): Promise<{ success: boolean; savedResponses: number }> {
  const res = await callApi(`survey-api/responses`, {
    method: 'POST',
    body: { surveyId, responses },
    accept: 'application/json',
  })

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to save responses: ${errorText}`)
  }

  return res.json()
}

// Get survey results
export async function getSurveyResults(attemptId: number): Promise<SurveyResults> {
  const res = await callApi(`survey-api/results/${attemptId}`, { method: 'GET' })

  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const text = await res.text()
    throw new Error(`Expected JSON but got ${ct}. ${text.slice(0, 200)}`)
  }

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to fetch results: ${errorText}`)
  }

  return res.json()
}

// Get latest survey results
export async function getLatestSurveyAttempt(studentId: string): Promise<SurveyAttempt> {
  // const res = await callApi(`survey-api/latest-attempt/${attemptId}`, { method: 'GET' })
  const res = await callApi(`survey-api/latest-attempt`, {
    method: 'POST',
    body: { studentId },
    accept: 'application/json',
  })
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    const text = await res.text()
    throw new Error(`Expected JSON but got ${ct}. ${text.slice(0, 200)}`)
  }

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Failed to fetch latest survey attempt: ${errorText}`)
  }

  return res.json()
}
